import { IconButton, Typography, Divider } from '@material-ui/core';
import { Edit, RadioButtonUnchecked } from '@material-ui/icons';
import {
  Color,
  ColorDynamic,
  Column,
  Columns,
  Inline,
  Stack,
} from '@superdispatch/ui';
import { TextBox } from '@superdispatch/ui-lab';
import { ReactNode } from 'react';
import { LoadDTO } from '../data/LoadDTO';
import { CheckIcon } from 'shared/icons/CheckIcon';

interface SubhauledPaymentStatusProps {
  load: LoadDTO;
  onEditPayment?: () => void;
  onEditMarkAsPaid?: () => void;
  customerPaymentDetails?: ReactNode;
  customerInvoiceDetails?: ReactNode;
  subhaulerInvoiceDetails?: ReactNode;
  subhaulerPaymentDetails?: ReactNode;
}

export function ViewLoadSubhauledPaymentStatus({
  load,
  onEditPayment,
  onEditMarkAsPaid,
  customerPaymentDetails,
  customerInvoiceDetails,
  subhaulerInvoiceDetails,
  subhaulerPaymentDetails,
}: SubhauledPaymentStatusProps) {
  const subhaulerInvoiceReceivedAt = !!load.subhauler_invoice?.received_at;
  const customerInvoiceReceivedAt = !!load.customer_invoice?.sent_at;
  const isSubhaulerBilled =
    load.subhauler_payment?.status === 'paid' ||
    load.subhauler_payment?.status === 'billed';
  const isCustomerBilled =
    load.customer_payment?.status === 'paid' ||
    load.customer_payment?.status === 'billed';

  return (
    <Stack space="small">
      <Typography variant="h6" color="textSecondary">
        Customer
      </Typography>

      {isCustomerBilled ? (
        <Stack space="xsmall">
          <Columns space="xxsmall" align="center">
            <Column width="fluid">
              <Inline space="xxsmall" verticalAlign="center">
                <CheckIcon fontSize="small" htmlColor={Color.Green300} />
                <TextBox variant="body-semibold" color="primary">
                  Customer Payment Received
                </TextBox>
                {onEditMarkAsPaid && (
                  <IconButton
                    size="small"
                    edge="start"
                    onClick={onEditMarkAsPaid}
                    aria-label="Edit invoice icon"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}
              </Inline>
            </Column>
          </Columns>

          {!!customerPaymentDetails && customerPaymentDetails}
        </Stack>
      ) : (
        <Inline space="xxsmall" verticalAlign="center">
          <RadioButtonUnchecked fontSize="small" color="action" />
          <TextBox color="primary" variant="body-semibold">
            Payment Not Received
          </TextBox>
        </Inline>
      )}

      <Stack space="xsmall">
        <Columns space="xsmall" align="center">
          <Column width="fluid">
            <Inline space="xxsmall" verticalAlign="center">
              {customerInvoiceReceivedAt ? (
                <CheckIcon fontSize="small" htmlColor={ColorDynamic.Green300} />
              ) : (
                <RadioButtonUnchecked fontSize="small" color="action" />
              )}
              <TextBox variant="body-semibold" color="primary">
                {customerInvoiceReceivedAt ? 'Billed' : 'Not Billed'}
              </TextBox>
              {customerInvoiceReceivedAt && onEditPayment && (
                <IconButton
                  size="small"
                  edge="start"
                  onClick={onEditPayment}
                  aria-label="Edit invoice icon"
                >
                  <Edit fontSize="small" />
                </IconButton>
              )}
            </Inline>
          </Column>
        </Columns>

        {!!customerInvoiceReceivedAt && customerInvoiceDetails}
      </Stack>

      <Divider />

      <Typography variant="h6" color="textSecondary">
        Sub-hauler
      </Typography>

      <Stack space="xsmall">
        <Columns space="xsmall" align="center">
          <Column width="fluid">
            <Inline space="xxsmall" verticalAlign="center">
              {isSubhaulerBilled ? (
                <CheckIcon fontSize="small" htmlColor={ColorDynamic.Green300} />
              ) : (
                <RadioButtonUnchecked fontSize="small" color="action" />
              )}
              <TextBox variant="body-semibold" color="primary">
                {formatSubhaulerPayment(load.subhauler_payment?.status)}
              </TextBox>
            </Inline>
          </Column>
        </Columns>

        {!!isSubhaulerBilled && subhaulerPaymentDetails}
      </Stack>

      <Stack space="xsmall">
        <Columns space="xsmall" align="center">
          <Column width="fluid">
            <Inline space="xxsmall" verticalAlign="center">
              {subhaulerInvoiceReceivedAt ? (
                <CheckIcon fontSize="small" htmlColor={ColorDynamic.Green300} />
              ) : (
                <RadioButtonUnchecked fontSize="small" color="action" />
              )}
              <TextBox variant="body-semibold" color="primary">
                {subhaulerInvoiceReceivedAt
                  ? 'Invoice Received'
                  : 'Invoice Not Received'}
              </TextBox>
            </Inline>
          </Column>
        </Columns>

        {!!subhaulerInvoiceReceivedAt && subhaulerInvoiceDetails}
      </Stack>
    </Stack>
  );
}

function formatSubhaulerPayment(status?: string) {
  switch (status) {
    case 'billed':
      return 'Billed';
    case 'paid':
      return 'Paid';
    case 'unpaid':
      return 'Not Paid';
    default:
      return status;
  }
}
